import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { UseQueryResult, UseMutationResult } from 'react-query';
import { useQuery, useMutation } from 'react-query';
import { invalidateRecentSurveysCache } from '@hotjar/webapp/hooks/useRecentSurveys';
import type { Clause } from '@hotjar/feedback/domain/Surveys/Responses/Filters/Clause';
import type { AxiosError } from 'axios';
import {
  deleteSurveyResponse,
  deleteSurveyWidget,
  getSurveyWidgets,
  getSurveyWidget,
  toggleSurveyStatus,
  saveSurveyWidget,
  getSurveyResponse,
  getSurveyResponses,
  sendResponseFeedback,
  getSurveyResponseCount,
  getSurveyQuestions,
  getSurveyBreakdown,
  getSurveyOverview,
} from './api';
import type { Survey, SurveyListItem } from './Survey/Survey';
import { invalidateFeedbackToolsCount } from '../common/query';
import type { SurveyResponseDto, SurveyResponsesDto } from './Responses/SurveyResponseDto';
import type { VotingAction } from '../common/Responses/VotingAction';
import type { SurveyQuestion } from './Questions';
import type { GetBreakdown, GetBreakdownResponse } from './Performance/Breakdown';
import type { GetOverview, GetOverviewResponse } from './Performance/Overview';

export const SURVEYS_QUERY_BASE_KEY = 'surveys';

export const SURVEYS_STALE_TIME = 1000 * 60 * 5;

const QUESTIONS_QUERY_BASE_KEY = 'questions';
const RESPONSES_QUERY_BASE_KEY = 'responses';

type GET_SURVEYS_KEY = [string, { siteId?: number }];

const fetchSurveyWidgets = ({ queryKey }: { queryKey: GET_SURVEYS_KEY }) => {
  const { siteId } = queryKey[1];
  if (siteId === undefined) throw new Error('Invalid site id');
  return getSurveyWidgets(siteId);
};

export const useSurveyWidgetsQuery = (siteId?: number): UseQueryResult<SurveyListItem[], Error> => {
  const hasSiteId = typeof siteId !== 'undefined';

  return useQuery([SURVEYS_QUERY_BASE_KEY, { siteId }], fetchSurveyWidgets, {
    enabled: hasSiteId,
    staleTime: SURVEYS_STALE_TIME,
  });
};

export const useSurveyWidgetQuery = (
  siteId?: number,
  surveyId?: number,
): UseQueryResult<Survey, AxiosError> => {
  const enabled = siteId !== undefined && surveyId !== undefined;
  return useQuery(
    [SURVEYS_QUERY_BASE_KEY, { siteId }, { surveyId }],
    () => {
      if (!enabled) throw new Error();
      return getSurveyWidget(siteId, surveyId);
    },
    {
      enabled,
      staleTime: SURVEYS_STALE_TIME,
      refetchOnWindowFocus: false,
      retry: (count, error) => count < 3 && error.response?.status !== 404,
    },
  );
};

export async function querySurveyWidgets(siteId: number): Promise<Survey[]> {
  return queryClient.fetchQuery([SURVEYS_QUERY_BASE_KEY, { siteId }], fetchSurveyWidgets, {
    staleTime: SURVEYS_STALE_TIME,
  });
}

const fetchDefaultQuery = async (siteId: number) => {
  const result = await querySurveyWidgets(siteId);
  return result.sort(
    (a, b) => Number(b.enabled) - Number(a.enabled) || b.created_epoch_time - a.created_epoch_time,
  )[0];
};

export async function queryDefaultSurvey(siteId: number): Promise<Survey> {
  return queryClient.fetchQuery([SURVEYS_QUERY_BASE_KEY, { siteId }, 'default'], () =>
    fetchDefaultQuery(siteId),
  );
}

export const useDefaultSurveyQuery = (
  siteId: number | undefined,
  isEnabled: boolean,
): UseQueryResult<Survey, Error> => {
  return useQuery(
    [SURVEYS_QUERY_BASE_KEY, { siteId }, 'default'],
    () => {
      if (siteId === undefined) throw new Error('siteId is not defined');
      return fetchDefaultQuery(siteId);
    },
    {
      enabled: isEnabled && siteId !== undefined,
      staleTime: SURVEYS_STALE_TIME,
    },
  );
};

export const useSurveyResponseQuery = (
  siteId: number | undefined,
  surveyId: number | undefined,
  responseId: number | undefined,
  enabled: boolean,
): UseQueryResult<SurveyResponseDto, Error> => {
  return useQuery(
    [RESPONSES_QUERY_BASE_KEY, { siteId }, { surveyId }, { responseId }],
    () => {
      if (!siteId || !surveyId || !responseId) throw new Error();
      return getSurveyResponse(siteId, surveyId, responseId);
    },
    {
      enabled: enabled && Boolean(siteId) && Boolean(surveyId) && Boolean(responseId),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );
};

const updateSurveyResponseCount = (siteId: number, surveyId: number, count: number) => {
  const surveyListQueryKey: GET_SURVEYS_KEY = [SURVEYS_QUERY_BASE_KEY, { siteId }];
  const existingSurveyList = queryClient.getQueryData<SurveyListItem[]>(surveyListQueryKey);
  if (!existingSurveyList) return;
  const surveyIndex = existingSurveyList.findIndex((survey) => survey.id === surveyId);
  if (surveyIndex === -1) return;

  // Cancel the query if it is fetching to prevent it overwriting our update
  const queryIsFetching = queryClient.getQueryState(surveyListQueryKey)?.isFetching;
  if (queryIsFetching) {
    queryClient.cancelQueries(surveyListQueryKey);
  }

  const newList = [...existingSurveyList];
  // eslint-disable-next-line camelcase
  newList[surveyIndex] = { ...existingSurveyList[surveyIndex], response_count: count };
  queryClient.setQueryData<SurveyListItem[]>(surveyListQueryKey, newList);

  // If we cancelled a fetch to make this update - trigger a refetch
  if (queryIsFetching) {
    queryClient.refetchQueries(surveyListQueryKey);
  }
};

export const useSurveyResponseCountQuery = (
  siteId: number | undefined,
  surveyId: number | undefined,
): UseQueryResult<number, Error> => {
  const enabled = siteId !== undefined && surveyId !== undefined;
  return useQuery(
    [RESPONSES_QUERY_BASE_KEY, { siteId }, { surveyId }, 'count'],
    async () => {
      if (!enabled) throw new Error();

      const result = await getSurveyResponseCount(siteId, surveyId, []);

      if (!result.success) throw new Error();

      updateSurveyResponseCount(siteId, surveyId, result.count);

      return result.count;
    },
    {
      enabled,
      staleTime: SURVEYS_STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      keepPreviousData: false,
    },
  );
};

export const useSurveyResponsesQuery = (
  siteId: number | undefined,
  surveyId: number | undefined,
  sort: string | undefined,
  clauses: Clause[] | undefined,
  pageNumber: number | undefined,
  pageSize: number | undefined,
): UseQueryResult<SurveyResponsesDto, Error> => {
  const enabled =
    siteId !== undefined &&
    surveyId !== undefined &&
    sort !== undefined &&
    clauses !== undefined &&
    pageNumber !== undefined &&
    pageSize !== undefined;
  return useQuery(
    [
      RESPONSES_QUERY_BASE_KEY,
      { siteId },
      { surveyId },
      { sort },
      { clauses },
      { pageNumber },
      { pageSize },
    ],
    async () => {
      if (!enabled) throw new Error();

      const responsesPromise = getSurveyResponses(
        siteId,
        surveyId,
        sort,
        clauses,
        pageNumber,
        pageSize,
      );

      const countPromise = getSurveyResponseCount(siteId, surveyId, clauses);

      const [responsesResult, countResult] = await Promise.all([responsesPromise, countPromise]);

      if (!responsesResult.success || !countResult.success) throw new Error();

      return { responses: responsesResult.responses, count: countResult.count };
    },
    {
      enabled,
      staleTime: SURVEYS_STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      keepPreviousData: true,
    },
  );
};

export const invalidateSurveyWidgets = (siteId: number): void => {
  queryClient.invalidateQueries([SURVEYS_QUERY_BASE_KEY, { siteId }]);
};

export const invalidateSurveyWidget = (siteId: number, surveyId: number): void => {
  queryClient.invalidateQueries([SURVEYS_QUERY_BASE_KEY, { siteId }]);
  queryClient.invalidateQueries([SURVEYS_QUERY_BASE_KEY, { siteId }, { surveyId }]);
};

export const invalidateSurveyResponses = (siteId: number, surveyId: number): void => {
  queryClient.invalidateQueries([RESPONSES_QUERY_BASE_KEY, { siteId }, { surveyId }]);
};

type DeleteSurveyVariables = { siteId: number; surveyId: number };

export const useDeleteSurveyWidgetMutation = (): UseMutationResult<
  void,
  unknown,
  DeleteSurveyVariables,
  unknown
> => {
  return useMutation(({ siteId, surveyId }) => deleteSurveyWidget(siteId, surveyId), {
    onSuccess: (_, { siteId, surveyId }) => {
      invalidateRecentSurveysCache(siteId);
      invalidateSurveyWidget(siteId, surveyId);
      invalidateFeedbackToolsCount(siteId, 'surveys');
    },
  });
};

type DeleteSurveyResponseVariables = { siteId: number; surveyId: number; responseId: number };

export const useDeleteSurveyResponseMutation = (): UseMutationResult<
  void,
  unknown,
  DeleteSurveyResponseVariables,
  unknown
> => {
  return useMutation(
    ({ siteId, surveyId, responseId }) => deleteSurveyResponse(siteId, surveyId, responseId),
    {
      onSuccess: (_, { siteId, surveyId }) => {
        invalidateRecentSurveysCache(siteId);
        invalidateSurveyWidget(siteId, surveyId);
        invalidateSurveyResponses(siteId, surveyId);
      },
    },
  );
};

type ToggleSurveyStatusVariables = { siteId: number; surveyId: number };

export const useToggleSurveyStatusMutation = (): UseMutationResult<
  void,
  unknown,
  ToggleSurveyStatusVariables,
  unknown
> => {
  return useMutation(({ siteId, surveyId }) => toggleSurveyStatus(siteId, surveyId), {
    onSuccess: (_, { siteId, surveyId }) => {
      invalidateRecentSurveysCache(siteId);
      invalidateSurveyWidget(siteId, surveyId);
    },
  });
};

type RenameSurveyVariables = {
  siteId: number;
  surveyId: number;
  name: string;
};

export const useRenameSurveyMutation = (): UseMutationResult<
  void,
  unknown,
  RenameSurveyVariables,
  unknown
> => {
  return useMutation(
    async ({ siteId, surveyId, name }) => {
      // It would be better if we had a dedicated rename endpoint but for now we can request a fresh copy of the widget
      const survey = await getSurveyWidget(siteId, surveyId);
      await saveSurveyWidget(siteId, { ...survey, name });
    },
    {
      onSuccess: (_, { siteId, surveyId }) => {
        invalidateRecentSurveysCache(siteId);
        invalidateSurveyWidget(siteId, surveyId);
      },
    },
  );
};

type ResponseFeedbackVariables = {
  votingAction: VotingAction;
  siteId: number;
  surveyId: number;
  responseId: number;
};

export const useResponseFeedbackMutation = (): UseMutationResult<
  void,
  unknown,
  ResponseFeedbackVariables,
  unknown
> => {
  return useMutation(
    ({ siteId, surveyId, responseId, votingAction }) =>
      sendResponseFeedback(votingAction, siteId, surveyId, responseId),
    {
      onSuccess: async (_, { siteId, surveyId }) => {
        // TODO: Ideally we would avoid invalidating the responses by updating the cached voting action values
        // This is complicated for us because there can be multiple cached queries (for different sort/filter/page configs)
        // For now, we can invalidate.
        queryClient.invalidateQueries([RESPONSES_QUERY_BASE_KEY, { siteId }, { surveyId }]);
      },
    },
  );
};

export const useSurveyQuestionsQuery = (
  siteId: number | undefined,
  surveyId: number | undefined,
): UseQueryResult<Array<SurveyQuestion>, Error> => {
  const enabled = siteId !== undefined && surveyId !== undefined;
  return useQuery(
    [QUESTIONS_QUERY_BASE_KEY, { siteId }, { surveyId }],
    async () => {
      if (!enabled) throw new Error();
      const result = await getSurveyQuestions(siteId, surveyId);
      return result.questions;
    },
    {
      enabled,
      staleTime: SURVEYS_STALE_TIME,
      refetchOnWindowFocus: false,
    },
  );
};

export const useSurveyOverview = ({
  siteId,
  surveyId,
}: GetOverview): UseQueryResult<GetOverviewResponse, AxiosError> => {
  const enabled = siteId !== undefined && surveyId !== undefined;

  return useQuery(
    ['surveyOverview', { siteId, surveyId }],
    () => {
      if (!enabled) throw new Error();
      return getSurveyOverview({ siteId, surveyId });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
    },
  );
};

export const useSurveyBreakdown = ({
  siteId,
  surveyId,
  filters,
}: GetBreakdown): UseQueryResult<GetBreakdownResponse, AxiosError> => {
  const enabled = siteId !== undefined && surveyId !== undefined;

  return useQuery(
    ['surveyBreakdown', { siteId, surveyId, filters }],
    () => {
      if (!enabled) throw new Error();
      return getSurveyBreakdown({ siteId, surveyId, filters });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
    },
  );
};

export const invalidateSurveyQuestions = (siteId: number, surveyId: number): void => {
  queryClient.invalidateQueries([QUESTIONS_QUERY_BASE_KEY, { siteId }, { surveyId }]);
};
